import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { setCurrentUserId } from '../utils/analytics';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = 'vibe-coach-token';
const USER_KEY = 'vibe-coach-user';
const ADMIN_KEY = 'vibe-coach-is-admin';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem(ADMIN_KEY) === 'true');

    // Logout function defined early for use in useEffect
    const clearAuth = useCallback(() => {
        setToken(null);
        setUser(null);
        setIsAdmin(false);
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem(ADMIN_KEY);
        // Clear user ID from analytics
        setCurrentUserId(null);
    }, []);

    // Check admin status
    const checkAdminStatus = useCallback(async (authToken) => {
        try {
            await axios.get(`${API}/admin/check`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            setIsAdmin(true);
            localStorage.setItem(ADMIN_KEY, 'true');
        } catch {
            setIsAdmin(false);
            localStorage.removeItem(ADMIN_KEY);
        }
    }, []);

    // Load stored auth on mount
    useEffect(() => {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const storedUser = localStorage.getItem(USER_KEY);
        
        if (storedToken && storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
                setToken(storedToken);
                // Link session to user for analytics attribution
                if (parsedUser?.id) {
                    setCurrentUserId(parsedUser.id);
                }
            } catch (e) {
                clearAuth();
            }
            
            // Verify token is still valid
            axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
            }).then(res => {
                setUser(res.data);
                localStorage.setItem(USER_KEY, JSON.stringify(res.data));
                // Link session to user for analytics attribution
                if (res.data?.id) {
                    setCurrentUserId(res.data.id);
                }
                // Check admin status
                checkAdminStatus(storedToken);
            }).catch(() => {
                // Token invalid, clear auth
                clearAuth();
            }).finally(() => {
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [clearAuth, checkAdminStatus]);

    const signup = async (email, password, name) => {
        const response = await axios.post(`${API}/auth/signup`, {
            email,
            password,
            name
        });
        
        const { access_token, user: userData } = response.data;
        
        // Set token first, then user
        localStorage.setItem(TOKEN_KEY, access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setToken(access_token);
        setUser(userData);
        
        // Link session to user for analytics (enables anonymous → registered attribution)
        if (userData?.id) {
            setCurrentUserId(userData.id);
        }
        
        // Check admin status
        checkAdminStatus(access_token);
        
        return userData;
    };

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, {
            email,
            password
        });
        
        const { access_token, user: userData } = response.data;
        
        // Set token first, then user
        localStorage.setItem(TOKEN_KEY, access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setToken(access_token);
        setUser(userData);
        
        // Link session to user for analytics (enables anonymous → registered attribution)
        if (userData?.id) {
            setCurrentUserId(userData.id);
        }
        
        // Check admin status
        checkAdminStatus(access_token);
        
        return userData;
    };

    const logout = useCallback(() => {
        clearAuth();
    }, [clearAuth]);

    // Use useCallback to ensure getAuthHeaders always has latest token
    const getAuthHeaders = useCallback(() => {
        const currentToken = token || localStorage.getItem(TOKEN_KEY);
        if (!currentToken) return {};
        return { Authorization: `Bearer ${currentToken}` };
    }, [token]);

    return (
        <AuthContext.Provider value={{
            user,
            token,
            loading,
            isAuthenticated: !!user && !!token,
            isAdmin,
            signup,
            login,
            logout,
            getAuthHeaders
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
